import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Avatar,
  Divider,
  Button,
  IconButton,
  Card,
  CardContent,
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as TransferIcon,
  Build as MaintenanceIcon,
  Warning as DamageIcon,
  QrCode2 as QrCodeIcon,
  Print as PrintIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  AttachMoney as MoneyIcon,
  Description as DocumentIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import QRCode from 'qrcode';

const AssetDetailsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [transferDialog, setTransferDialog] = useState(false);
  const [maintenanceDialog, setMaintenanceDialog] = useState(false);
  const [damageDialog, setDamageDialog] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [employees, setEmployees] = useState([]);
  const [transferData, setTransferData] = useState({
    newEmployeeId: '',
    transferReason: '',
    transferDate: new Date().toISOString().split('T')[0]
  });
  const [maintenanceData, setMaintenanceData] = useState({
    description: '',
    cost: '',
    performedBy: '',
    nextMaintenanceDate: ''
  });
  const [damageData, setDamageData] = useState({
    damageDescription: '',
    damageAmount: '',
    recoveryAmount: '',
    recoveryStatus: 'PENDING'
  });

  useEffect(() => {
    fetchAssetDetails();
    fetchEmployees();
  }, [id]);

  useEffect(() => {
    if (asset) {
      generateQRCode();
    }
  }, [asset]);

  const fetchAssetDetails = async () => {
    try {
      const response = await axios.get(`/api/employee-asset/${id}`);
      if (response.data.success) {
        setAsset(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching asset:', error);
      Swal.fire('Error', 'Failed to load asset details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get('/employee/getAllEmployees');
      if (Array.isArray(response.data)) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const generateQRCode = async () => {
    try {
      const qrData = JSON.stringify({
        assetId: asset._id,
        assetCode: asset.assetCode,
        assetName: asset.assetName,
        serialNumber: asset.serialNumber,
        type: asset.assetType
      });
      const url = await QRCode.toDataURL(qrData, { width: 300 });
      setQrCodeUrl(url);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleTransfer = async () => {
    try {
      const response = await axios.post(`/api/employee-asset/transfer/${id}`, transferData);
      if (response.data.success) {
        Swal.fire('Success', 'Asset transferred successfully', 'success');
        setTransferDialog(false);
        fetchAssetDetails();
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Transfer failed', 'error');
    }
  };

  const handleMaintenance = async () => {
    try {
      const response = await axios.post(`/api/employee-asset/maintenance/${id}`, maintenanceData);
      if (response.data.success) {
        Swal.fire('Success', 'Maintenance record added', 'success');
        setMaintenanceDialog(false);
        fetchAssetDetails();
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to add maintenance', 'error');
    }
  };

  const handleDamage = async () => {
    try {
      const response = await axios.post(`/api/employee-asset/damage/${id}`, damageData);
      if (response.data.success) {
        Swal.fire('Success', 'Damage reported successfully', 'success');
        setDamageDialog(false);
        fetchAssetDetails();
      }
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to report damage', 'error');
    }
  };

  const handleReturn = async () => {
    const result = await Swal.fire({
      title: 'Return Asset?',
      text: 'Confirm asset return from employee',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Return',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(`/api/employee-asset/return/${id}`, {
          returnCondition: 'GOOD',
          returnNotes: 'Asset returned'
        });
        if (response.data.success) {
          Swal.fire('Success', 'Asset returned successfully', 'success');
          fetchAssetDetails();
        }
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Return failed', 'error');
      }
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Asset?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`/api/employee-asset/${id}`);
        if (response.data.success) {
          Swal.fire('Deleted', 'Asset deleted successfully', 'success');
          navigate('/userdashboard/staff/hrm/assets');
        }
      } catch (error) {
        Swal.fire('Error', error.response?.data?.message || 'Delete failed', 'error');
      }
    }
  };

  const printQRCode = () => {
    const printWindow = window.open('', '', 'width=400,height=500');
    printWindow.document.write(`
      <html>
        <head>
          <title>Asset QR Code - ${asset.assetCode}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              text-align: center; 
              padding: 20px;
            }
            h2 { margin: 10px 0; }
            img { margin: 20px 0; }
            .info { margin: 10px 0; font-size: 14px; }
          </style>
        </head>
        <body>
          <h2>${asset.assetName}</h2>
          <div class="info"><strong>Asset Code:</strong> ${asset.assetCode}</div>
          <div class="info"><strong>Serial Number:</strong> ${asset.serialNumber || 'N/A'}</div>
          <div class="info"><strong>Type:</strong> ${asset.assetType}</div>
          <img src="${qrCodeUrl}" alt="QR Code" />
          <div class="info">Scan to view asset details</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusColor = (status) => {
    const colors = {
      ASSIGNED: 'primary',
      RETURNED: 'success',
      DAMAGED: 'error',
      LOST: 'error',
      UNDER_MAINTENANCE: 'warning'
    };
    return colors[status] || 'default';
  };

  const getConditionColor = (condition) => {
    const colors = {
      EXCELLENT: 'success',
      GOOD: 'primary',
      FAIR: 'warning',
      POOR: 'error',
      DAMAGED: 'error'
    };
    return colors[condition] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Typography>Loading asset details...</Typography>
      </Box>
    );
  }

  if (!asset) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Asset not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/userdashboard/staff/hrm/assets')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>Asset Details</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/userdashboard/staff/hrm/assets/edit/${id}`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Info Card */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'start', mb: 3 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {asset.assetName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {asset.assetCode} • {asset.serialNumber || 'No Serial Number'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Chip label={asset.assetType} size="small" />
                  <Chip label={asset.status} color={getStatusColor(asset.status)} size="small" />
                  <Chip label={asset.condition} color={getConditionColor(asset.condition)} size="small" />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Asset Information */}
            <Grid container spacing={3}>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Brand</Typography>
                <Typography variant="body1" fontWeight={500}>{asset.brand || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Model</Typography>
                <Typography variant="body1" fontWeight={500}>{asset.model || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Purchase Value</Typography>
                <Typography variant="body1" fontWeight={500}>₹{asset.purchaseValue?.toLocaleString() || 0}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Current Value</Typography>
                <Typography variant="body1" fontWeight={500}>₹{asset.currentValue?.toLocaleString() || 0}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Depreciation Rate</Typography>
                <Typography variant="body1" fontWeight={500}>{asset.depreciationRate || 0}%</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Assigned Date</Typography>
                <Typography variant="body1" fontWeight={500}>
                  {new Date(asset.assignedDate).toLocaleDateString()}
                </Typography>
              </Grid>
            </Grid>

            {/* Employee Info */}
            {asset.employeeId && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Assigned To
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Avatar src={asset.employeeId.imageUrl} sx={{ width: 50, height: 50 }}>
                    {asset.employeeId.staff_name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {asset.employeeId.staff_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {asset.employeeId.emp_id} • {asset.employeeId.department?.departmentName || 'No Department'}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}

            {asset.notes && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {asset.notes}
                </Typography>
              </>
            )}
          </Paper>

          {/* Tabs for History */}
          <Paper>
            <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
              <Tab label="Maintenance History" icon={<HistoryIcon />} iconPosition="start" />
              <Tab label="Documents" icon={<DocumentIcon />} iconPosition="start" />
              <Tab label="Financial" icon={<MoneyIcon />} iconPosition="start" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Maintenance History Tab */}
              {activeTab === 0 && (
                <Box>
                  {asset.maintenanceHistory && asset.maintenanceHistory.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Cost</TableCell>
                            <TableCell>Performed By</TableCell>
                            <TableCell>Next Maintenance</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {asset.maintenanceHistory.map((record, index) => (
                            <TableRow key={index}>
                              <TableCell>{new Date(record.maintenanceDate).toLocaleDateString()}</TableCell>
                              <TableCell>{record.description}</TableCell>
                              <TableCell>₹{record.cost?.toLocaleString()}</TableCell>
                              <TableCell>{record.performedBy}</TableCell>
                              <TableCell>
                                {record.nextMaintenanceDate 
                                  ? new Date(record.nextMaintenanceDate).toLocaleDateString()
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No maintenance history available</Alert>
                  )}
                </Box>
              )}

              {/* Documents Tab */}
              {activeTab === 1 && (
                <Box>
                  {asset.documents && asset.documents.length > 0 ? (
                    <Grid container spacing={2}>
                      {asset.documents.map((doc, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Card>
                            <CardContent>
                              <Typography variant="subtitle2">{doc.documentType}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {doc.fileName}
                              </Typography>
                              <Button
                                size="small"
                                href={doc.fileUrl}
                                target="_blank"
                                sx={{ mt: 1 }}
                              >
                                View
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Alert severity="info">No documents uploaded</Alert>
                  )}
                </Box>
              )}

              {/* Financial Tab */}
              {activeTab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">Purchase Value</Typography>
                        <Typography variant="h6">₹{asset.purchaseValue?.toLocaleString() || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">Current Value</Typography>
                        <Typography variant="h6">₹{asset.currentValue?.toLocaleString() || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">Depreciation</Typography>
                        <Typography variant="h6">
                          ₹{((asset.purchaseValue || 0) - (asset.currentValue || 0)).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle2" color="text.secondary">Damage/Loss Amount</Typography>
                        <Typography variant="h6">₹{asset.damageAmount?.toLocaleString() || 0}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* QR Code Card */}
          <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Asset QR Code
            </Typography>
            {qrCodeUrl && (
              <>
                <Box sx={{ my: 2 }}>
                  <img src={qrCodeUrl} alt="QR Code" style={{ width: '100%', maxWidth: 250 }} />
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={printQRCode}
                  fullWidth
                >
                  Print QR Code
                </Button>
              </>
            )}
          </Paper>

          {/* Actions Card */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<TransferIcon />}
                onClick={() => setTransferDialog(true)}
                disabled={asset.status !== 'ASSIGNED'}
                fullWidth
              >
                Transfer Asset
              </Button>
              <Button
                variant="outlined"
                startIcon={<MaintenanceIcon />}
                onClick={() => setMaintenanceDialog(true)}
                fullWidth
              >
                Add Maintenance
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<DamageIcon />}
                onClick={() => setDamageDialog(true)}
                fullWidth
              >
                Report Damage
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleReturn}
                disabled={asset.status !== 'ASSIGNED'}
                fullWidth
              >
                Return Asset
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Transfer Dialog */}
      <Dialog open={transferDialog} onClose={() => setTransferDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Asset</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label="New Employee"
              value={transferData.newEmployeeId}
              onChange={(e) => setTransferData({ ...transferData, newEmployeeId: e.target.value })}
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.staff_name} ({emp.emp_id})
                </option>
              ))}
            </TextField>
            <TextField
              type="date"
              label="Transfer Date"
              value={transferData.transferDate}
              onChange={(e) => setTransferData({ ...transferData, transferDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              multiline
              rows={3}
              label="Transfer Reason"
              value={transferData.transferReason}
              onChange={(e) => setTransferData({ ...transferData, transferReason: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(false)}>Cancel</Button>
          <Button onClick={handleTransfer} variant="contained">Transfer</Button>
        </DialogActions>
      </Dialog>

      {/* Maintenance Dialog */}
      <Dialog open={maintenanceDialog} onClose={() => setMaintenanceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Maintenance Record</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              multiline
              rows={3}
              label="Description"
              value={maintenanceData.description}
              onChange={(e) => setMaintenanceData({ ...maintenanceData, description: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="Cost"
              value={maintenanceData.cost}
              onChange={(e) => setMaintenanceData({ ...maintenanceData, cost: e.target.value })}
              fullWidth
            />
            <TextField
              label="Performed By"
              value={maintenanceData.performedBy}
              onChange={(e) => setMaintenanceData({ ...maintenanceData, performedBy: e.target.value })}
              fullWidth
            />
            <TextField
              type="date"
              label="Next Maintenance Date"
              value={maintenanceData.nextMaintenanceDate}
              onChange={(e) => setMaintenanceData({ ...maintenanceData, nextMaintenanceDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaintenanceDialog(false)}>Cancel</Button>
          <Button onClick={handleMaintenance} variant="contained">Add Record</Button>
        </DialogActions>
      </Dialog>

      {/* Damage Dialog */}
      <Dialog open={damageDialog} onClose={() => setDamageDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report Damage</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              multiline
              rows={3}
              label="Damage Description"
              value={damageData.damageDescription}
              onChange={(e) => setDamageData({ ...damageData, damageDescription: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="Damage Amount"
              value={damageData.damageAmount}
              onChange={(e) => setDamageData({ ...damageData, damageAmount: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="Recovery Amount"
              value={damageData.recoveryAmount}
              onChange={(e) => setDamageData({ ...damageData, recoveryAmount: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Recovery Status"
              value={damageData.recoveryStatus}
              onChange={(e) => setDamageData({ ...damageData, recoveryStatus: e.target.value })}
              SelectProps={{ native: true }}
              fullWidth
            >
              <option value="PENDING">Pending</option>
              <option value="PARTIAL">Partial</option>
              <option value="FULL">Full</option>
              <option value="WAIVED">Waived</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDamageDialog(false)}>Cancel</Button>
          <Button onClick={handleDamage} variant="contained" color="warning">Report</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetDetailsView;
